import {
  CSSProperties,
  HTMLAttributes,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/router'
import { Bounce, toast } from 'react-toastify'

import type {
  MessageDecoder,
  MessageEncoder,
  TrackReferenceOrPlaceholder,
} from '@livekit/components-core'
import { isEqualTrackRef, isTrackReference, isWeb, log } from '@livekit/components-core'
import { ConnectionQuality, RoomEvent, Track } from 'livekit-client'

import { useLocalParticipant } from '@/src'
import { MessageFormatter, Toast } from '@/src/components'
import { Toast as WarningToast } from '@/src/components/Toast/Toast'
import {
  CarouselLayout,
  ConnectionStateToast,
  FocusLayout,
  FocusLayoutContainer,
  GridLayout,
  LayoutContextProvider,
  ParticipantTile,
  RoomAudioRenderer,
} from '@/src/components'
import { PictureInPicture } from '@/src/components/pip/PictureInPicture'
import { CandidateSkills } from '@/src/components/room/CandidateSkills/CandidateSkills'
import { Chat } from '@/src/components/room/Chat/Chat'
import TextAgentQuestionsPanel from './TextAgentQuestionsPanel'
import { ParticipantList } from './ParticipantList'
import { ControlBar, ControlBarControls } from './ControlBar'
import {
  isAvatarRendererParticipant,
  shouldShowFedorParticipant,
} from './avatarParticipantVisibility'
import { ToggleAudio } from '@/src/components/participant/Guest/ToggleAudio'

import { useCreateLayoutContext, useRoomContext } from '@/src/context'

import type { WidgetState } from '@/src/lib/types'
import {
  readRecordingTarget,
  shouldIncludeParticipantInTargetRecording,
} from '@/src/lib/recordingTarget'
import { isCodecSwitchPinSuppressed } from '@/src/lib/screenShareCodec'

import {
  usePinnedTracks,
  useTracks,
  useConnectionQualityIndicator,
  UseTracksOptions,
} from '@/src/hooks'
import { useWarnAboutMissingStyles } from '@/src/hooks/useWarnAboutMissingStyles'
import { useParticipantPermissionsNotification } from '@/src/hooks/useParticipantPermissionsNotification'
import { useGroupChangeNotification } from '@/src/hooks/useGroupChangeNotification'
import {
  isFedorAgentParticipant,
  isVoiceAgentRoomType,
  useAgentReadiness,
} from '@/src/hooks/room/useAgentReadiness'

import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import { selectBranding, selectIsAdmin } from '@/src/store/slices/meetData'
import { selectIsOpenChat, setIsOpenChat } from '@/src/store/slices/chat'
import { selectRoomType } from '@/src/store/slices/room'

/**
 * @public
 */
export interface VideoConferenceProps extends HTMLAttributes<HTMLDivElement> {
  chatMessageFormatter?: MessageFormatter
  chatMessageEncoder?: MessageEncoder
  chatMessageDecoder?: MessageDecoder
  data?: Object
  controls?: ControlBarControls
  /**
   * Скрывает штатную плашку «Подключение», пока поверх уже идёт тост retry.
   */
  hideConnectionStateToast?: boolean
  /** Убирает общий Poor-toast, когда владелец звонка уже показывает VPN-подсказку. */
  hidePoorConnectionToast?: boolean
  /** @alpha */
}

const defaultControlBarControls: ControlBarControls = { chat: true }

/**
 * The `VideoConference` ready-made component is your drop-in solution for a classic video conferencing application.
 * It provides functionality such as focusing on one participant, grid view with pagination to handle large numbers
 * of participants, basic non-persistent chat, screen sharing, and more.
 *
 * @remarks
 * The component is implemented with other LiveKit components like `FocusContextProvider`,
 * `GridLayout`, `ControlBar`, `FocusLayoutContainer` and `FocusLayout`.
 * You can use this components as a starting point for your own custom video conferencing application.
 *
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <VideoConference />
 * <LiveKitRoom>
 * ```
 * @public
 */

const tracksOptions: UseTracksOptions = {
  onlySubscribed: false,
  updateOnlyOn: [RoomEvent.ActiveSpeakersChanged, RoomEvent.ParticipantPermissionsChanged],
}

const AGENT_INTERVIEW_START_TOPIC = 'agent-interview-start'

// ID для участников в основной комнате (без группы)
const MAIN_ROOM_GROUP_ID = 'main_room'

// Получает groupId участника из metadata или атрибутов
// Сначала проверяет participantGroupMap в metadata (актуальные данные),
// затем fallback на атрибуты участника.
// Если групп в комнате нет (metadata.groups пустой или отсутствует),
// считаем, что все находятся в одном общем зале.
const getParticipantGroupId = (
  participant: TrackReferenceOrPlaceholder['participant'] | null | undefined,
  roomMetadata?: string,
) => {
  if (!participant) return MAIN_ROOM_GROUP_ID

  let participantGroupMap: Record<string, string | null> = {}
  let hasGroups = false

  if (roomMetadata) {
    try {
      const parsed = JSON.parse(roomMetadata)
      participantGroupMap = parsed.participantGroupMap || {}
      const groupsArray = Array.isArray(parsed.groups) ? parsed.groups : []
      hasGroups = groupsArray.length > 0
    } catch (e) {
      console.error('[VideoConference] Error parsing room metadata for groups:', e)
    }
  }

  // Если групп нет, все участники считаются в общем зале
  if (!hasGroups) {
    return MAIN_ROOM_GROUP_ID
  }

  if (participant.identity in participantGroupMap) {
    const groupId = participantGroupMap[participant.identity]
    if (groupId === null || groupId === undefined || groupId === '') {
      return MAIN_ROOM_GROUP_ID
    }
    return groupId
  }

  const rawGroupId = (participant.attributes as any)?.groupId
  if (typeof rawGroupId === 'string') {
    const trimmed = rawGroupId.trim()
    if (trimmed.length === 0 || trimmed === 'null' || trimmed === 'undefined') {
      return MAIN_ROOM_GROUP_ID
    }
    return trimmed
  }

  return MAIN_ROOM_GROUP_ID
}

export const VideoConference = memo(function MemoVideoConference({
  data,
  chatMessageFormatter,
  chatMessageDecoder,
  chatMessageEncoder,
  controls: controlsProp,
  hideConnectionStateToast = false,
  hidePoorConnectionToast = false,
  ...props
}: VideoConferenceProps) {
  const { t } = useTranslation()
  const lastAutoFocusedScreenShareTrack = useRef<TrackReferenceOrPlaceholder | null>(null)
  const [directorMuted, setDirectorMuted] = useState<boolean>(false)
  const [badInternet, setBadInternet] = useState<boolean>(false)
  const [isFocusLockActive, setIsFocusLockActive] = useState<boolean>(false)
  const [roomMetadataVersion, setRoomMetadataVersion] = useState(0)
  const [isQuestionsPanelOpen, setIsQuestionsPanelOpen] = useState(false)
  const [isInterviewStarted, setIsInterviewStarted] = useState(false)
  const [isInterviewStarting, setIsInterviewStarting] = useState(false)

  const layoutContext = useCreateLayoutContext()
  const room = useRoomContext()
  const dispatch = useAppDispatch()

  const router = useRouter()
  const { id, source } = router.query
  const path = router.asPath

  const [participantTracks, setParticipantTracks] = useState([])

  const { localParticipant } = useLocalParticipant()
  const storedIsAdmin = useAppSelector(selectIsAdmin)
  const isAdmin = localParticipant?.attributes?.role !== 'company_participant' && storedIsAdmin
  const isChatOpen = useAppSelector(selectIsOpenChat)
  const roomType = useAppSelector(selectRoomType)
  const { isAgentReady } = useAgentReadiness(room, roomType)
  const isVoiceAgentRoom = isVoiceAgentRoomType(roomType)
  const isLocalRecorder = localParticipant.permissions?.recorder === true
  const shouldShowFedor = isVoiceAgentRoom || isLocalRecorder
  const showAgentOverlay = isVoiceAgentRoom && (!isAgentReady || !isInterviewStarted)
  const showInterviewStart = isVoiceAgentRoom && isAgentReady && !isInterviewStarted

  const shouldShowControlBar = useMemo(() => {
    const permissions = localParticipant?.permissions
    if (!permissions) return false
    
    const isAgentOrRecorder = permissions.agent || permissions.recorder
    const canShowControls = permissions.canPublish || permissions.hidden
    
    return canShowControls && !isAgentOrRecorder
  }, [localParticipant?.permissions])

  useEffect(() => {
    const pTracks = []

    if (path.includes('director')) {
      pTracks.push({ source, withPlaceholder: true })
    } else {
      pTracks.push(
        { source: Track.Source.Camera, withPlaceholder: true },
        { source: Track.Source.ScreenShare, withPlaceholder: false },
      )
    }

    setParticipantTracks(pTracks)
  }, [path, source])

  const roomTracks = useTracks(participantTracks, tracksOptions)
  const hasAvatarRenderer = roomTracks.some((el) =>
    shouldShowFedor &&
    !el.participant.permissions?.recorder &&
    isAvatarRendererParticipant(el.participant),
  )

  const allTracks = roomTracks
    .filter((el) => el.participant.permissions?.canPublish === true)
    .filter((el) => !(showAgentOverlay && isFedorAgentParticipant(el.participant)))
    .filter((el) => {
      const participant = el.participant
      const isRecorder = participant.permissions?.recorder === true
      const isAvatarRenderer = isAvatarRendererParticipant(participant)

      // Если 3D renderer не подключился, оставляем в сетке статичного Фёдора.
      if (
        shouldShowFedor &&
        !isRecorder &&
        isFedorAgentParticipant(participant)
      ) {
        return shouldShowFedorParticipant(isAvatarRenderer, hasAvatarRenderer)
      }

      const isTechnicalAgent =
        participant.isAgent === true ||
        participant.permissions?.agent === true ||
        isRecorder ||
        participant.identity?.startsWith('agent-')

      if (isTechnicalAgent) {
        return false
      }

      if (path.includes('director')) {
        return participant.identity === id
      }

      return true
    })

  /**
   * Фильтруем видеодорожки по группам:
   * - Участник видит только тех, кто находится в той же группе, что и он сам.
   * - Участники без группы считаются в основном зале (MAIN_ROOM_GROUP_ID).
   * - Для агентов и рекордеров (egress/director) фильтрация не применяется, они видят всех.
   */
  const tracks = useMemo(() => {
    if (!localParticipant || !room) return allTracks

    const recordingTarget = readRecordingTarget(localParticipant.attributes)
    if (
      localParticipant.permissions?.recorder &&
      recordingTarget.recordingCandidateKey
    ) {
      let participantGroupMap: Record<string, string | null> | undefined
      try {
        participantGroupMap = room.metadata
          ? JSON.parse(room.metadata).participantGroupMap
          : undefined
      } catch {
        participantGroupMap = undefined
      }

      return allTracks.filter((trackRef) =>
        shouldIncludeParticipantInTargetRecording(
          trackRef.participant,
          recordingTarget,
          participantGroupMap,
        ),
      )
    }

    // Агенты и рекордеры видят всех независимо от групп
    if (localParticipant.permissions?.agent || localParticipant.permissions?.recorder) {
      return allTracks
    }

    const metadata = room.metadata
    const localGroupId = getParticipantGroupId(localParticipant, metadata)

    let filtered = allTracks.filter((trackRef) => {
      const remoteGroupId = getParticipantGroupId(trackRef.participant, metadata)
      return localGroupId === remoteGroupId
    })

    // В мега-комнатах показываем агентов и участников с hidden: false (не-скрытых)
    if (path.includes('/mega/')) {
      filtered = filtered.filter(
        (trackRef) =>
          trackRef.participant.permissions?.agent === true ||
          trackRef.participant.permissions?.hidden === false,
      )
    }

    return filtered
  }, [allTracks, localParticipant, room, roomMetadataVersion, path])

  const widgetUpdate = useCallback((state: WidgetState) => {
    log.debug('updating widget state', state)
  }, [])

  const handleStartInterview = useCallback(async () => {
    if (!room || isInterviewStarting) return

    setIsInterviewStarting(true)

    try {
      try {
        await room.localParticipant.setMicrophoneEnabled(true)
      } catch (micError) {
        console.error('[VideoConference] Failed to enable microphone:', micError)

        toast.error(
          (content) => (
            <WarningToast title={content?.toastProps?.ariaLabel} description={content?.data} />
          ),
          {
            ariaLabel: t('toast.error'),
            data: t('interviewMicrophoneEnableFailed'),
            role: 'alert',
            position: 'bottom-right',
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
            theme: 'dark',
            transition: Bounce,
          },
        )
      }

      await room.localParticipant.publishData(
        new TextEncoder().encode(
          JSON.stringify({
            type: 'interview_start',
            participantIdentity: room.localParticipant.identity,
            startedAt: Date.now(),
          }),
        ),
        {
          reliable: true,
          topic: AGENT_INTERVIEW_START_TOPIC,
        },
      )
      setIsInterviewStarted(true)
    } catch (error) {
      console.error('[VideoConference] Failed to start interview:', error)
    } finally {
      setIsInterviewStarting(false)
    }
  }, [isInterviewStarting, room, t])

  const screenShareTracks = useMemo(
    () =>
      tracks
        .filter(isTrackReference)
        .filter((track) => track.publication.source === Track.Source.ScreenShare),
    [tracks],
  )

  const focusTrack = usePinnedTracks(layoutContext)?.[0]
  const carouselTracks = useMemo(
    () => tracks.filter((track) => !isEqualTrackRef(track, focusTrack)),
    [tracks, focusTrack],
  )

  useEffect(() => {
    if (!room) return

    const handleMetadataChanged = (metadata: string) => {
      try {
        const parsed = JSON.parse(metadata)
        setIsFocusLockActive(parsed.focusLockEnabled === true)
        
        if (parsed.focusLockEnabled === true) {
          const { participantIdentity, trackSource } = parsed.lockedFocus || {}
          
          if (participantIdentity && trackSource) {
            const targetTrack = tracks.find(
              (track) =>
                isTrackReference(track) &&
                track.participant.identity === participantIdentity &&
                track.publication.source === trackSource
            )
            
            let trackToFocus = targetTrack
            if (!targetTrack) {
              const placeholderTrack = tracks.find(
                (track) =>
                  track.participant?.identity === participantIdentity &&
                  track.source === trackSource
              )
              
              if (placeholderTrack) {
                trackToFocus = placeholderTrack
              }
            }
            
            if (trackToFocus) {
              const currentFocus = layoutContext.pin.state?.[0]
              let isSameTrack = false
              
              if (currentFocus) {
                if (isTrackReference(currentFocus)) {
                  isSameTrack = currentFocus.participant.identity === participantIdentity &&
                    currentFocus.publication.source === trackSource
                } else {
                  isSameTrack = currentFocus.participant?.identity === participantIdentity &&
                    currentFocus.source === trackSource
                }
              }
              
              if (!isSameTrack) {
                layoutContext.pin.dispatch?.({ msg: 'set_pin', trackReference: trackToFocus })
              }
            }
          } else if (layoutContext.pin.state?.length) {
            layoutContext.pin.dispatch?.({ msg: 'clear_pin' })
          }
        }
      } catch (e) {
        console.error('Error parsing room metadata:', e)
      }
    }

    room.on(RoomEvent.RoomMetadataChanged, handleMetadataChanged)

    if (room.metadata) {
      handleMetadataChanged(room.metadata)
    }

    return () => {
      room.off(RoomEvent.RoomMetadataChanged, handleMetadataChanged)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, tracks])

  // Следим за изменением метаданных комнаты (groups, participantGroupMap и т.п.),
  // чтобы пересчитать фильтрацию треков по группам сразу при обновлении.
  useEffect(() => {
    if (!room) return

    const handleMetadataChanged = () => {
      setRoomMetadataVersion((prev) => prev + 1)
    }

    room.on(RoomEvent.RoomMetadataChanged, handleMetadataChanged)

    // Первичное значение при монтировании
    if (room.metadata) {
      handleMetadataChanged()
    }

    return () => {
      room.off(RoomEvent.RoomMetadataChanged, handleMetadataChanged)
    }
  }, [room])

  useEffect(() => {
    // If screen share tracks are published, and no pin is set explicitly, auto set the screen share.
    // But only if focus lock is not active
    if (path.includes('director')) {
      setDirectorMuted(true)
    }

    if (isFocusLockActive) {
      // Don't auto-focus screen share when focus lock is active
      return
    }

    if (
      screenShareTracks.some((track) => track.publication.isSubscribed) &&
      lastAutoFocusedScreenShareTrack.current === null
    ) {
      log.debug('Auto set screen share focus:', { newScreenShareTrack: screenShareTracks[0] })
      layoutContext.pin.dispatch?.({ msg: 'set_pin', trackReference: screenShareTracks[0] })
      lastAutoFocusedScreenShareTrack.current = screenShareTracks[0]
    } else if (lastAutoFocusedScreenShareTrack.current) {
      const focused = lastAutoFocusedScreenShareTrack.current
      const current = screenShareTracks.find(
        (track) =>
          track.participant.identity === focused.participant?.identity &&
          track.publication.source === focused.publication?.source,
      )

      if (current && current.publication.trackSid !== focused.publication?.trackSid) {
        // Pin хранит ссылку на публикацию со старым SID. Одного обновления ref
        // недостаточно: переносим развёрнутый показ на новую публикацию.
        // Явно выбранную пользователем другую плитку не перехватываем.
        if (!focusTrack || isEqualTrackRef(focusTrack, focused)) {
          layoutContext.pin.dispatch?.({ msg: 'set_pin', trackReference: current })
        }

        lastAutoFocusedScreenShareTrack.current = current
      } else if (!current && !isCodecSwitchPinSuppressed(focused.participant?.identity ?? '')) {
        // Переход кодека замораживает pin: снятие публикации — не конец показа.
        log.debug('Auto clearing screen share focus.')
        layoutContext.pin.dispatch?.({ msg: 'clear_pin' })
        lastAutoFocusedScreenShareTrack.current = null
      }
    }
  }, [screenShareTracks, layoutContext.pin, path, isFocusLockActive, focusTrack])

  const { quality } = useConnectionQualityIndicator({
    participant: localParticipant,
  })

  useEffect(() => {
    if (quality === ConnectionQuality.Poor) {
      setBadInternet(true)
    } else {
      setBadInternet(false)
    }
  }, [quality])

  const { text_primary_color } = useAppSelector(selectBranding)

  const toastStyles: CSSProperties = useMemo(
    () => ({
      zIndex: '9999',
      textAlign: 'center',
      color: `${text_primary_color}`,
    }),
    [text_primary_color],
  )

  const participantListStyles: CSSProperties = useMemo(
    () => ({ display: layoutContext.widget.state.showParticipants ? 'flex' : 'none' }),
    [layoutContext.widget.state.showParticipants],
  )

  const canShowQuestionsPanel = useMemo(
    () => shouldShowControlBar && isAdmin && roomType === 'textAssistant',
    [isAdmin, roomType, shouldShowControlBar],
  )

  const handleToggleQuestionsPanel = useCallback(() => {
    setIsQuestionsPanelOpen((previous) => {
      const next = !previous

      if (next) {
        dispatch(setIsOpenChat(false))
        layoutContext.widget.dispatch?.({ msg: 'hide_participants' })
      }

      return next
    })
  }, [dispatch, layoutContext.widget])

  useEffect(() => {
    if (!canShowQuestionsPanel && isQuestionsPanelOpen) {
      setIsQuestionsPanelOpen(false)
    }
  }, [canShowQuestionsPanel, isQuestionsPanelOpen])

  useEffect(() => {
    if (
      isQuestionsPanelOpen &&
      (isChatOpen || layoutContext.widget.state.showParticipants)
    ) {
      setIsQuestionsPanelOpen(false)
    }
  }, [isChatOpen, isQuestionsPanelOpen, layoutContext.widget.state.showParticipants])

  useWarnAboutMissingStyles()
  useParticipantPermissionsNotification()
  useGroupChangeNotification()

  return (
    <div
      className="lk-video-conference"
      style={{
        ...props.style,
        overflowY: 'hidden',
      }}
      {...props}
    >
      <RoomAudioRenderer muted={directorMuted} />
      {!hideConnectionStateToast && <ConnectionStateToast />}

      {badInternet && !directorMuted && !hidePoorConnectionToast && (
        <Toast
          style={toastStyles}
          className="lk-toast-connection-state lk-toast-connection-state lk-toast-internet-state"
        >
          {t('notStable')}
        </Toast>
      )}

      {isWeb() && (
        <LayoutContextProvider value={layoutContext} onWidgetChange={widgetUpdate}>
          <div
            className="lk-video-conference-inner"
            style={{ position: 'relative', overflow: 'hidden' }}
          >
            {!focusTrack ? (
              <div
                className="lk-grid-layout-wrapper"
                style={localParticipant?.isAgent ? { height: '100%' } : {}}
              >
                <GridLayout tracks={tracks}>
                  <ParticipantTile />
                </GridLayout>
              </div>
            ) : (
              <div className="lk-focus-layout-wrapper">
                <FocusLayoutContainer>
                  <CarouselLayout tracks={carouselTracks}>
                    <ParticipantTile />
                  </CarouselLayout>
                  {focusTrack && <FocusLayout trackRef={focusTrack} />}
                </FocusLayoutContainer>
              </div>
            )}

            <CandidateSkills />

            {shouldShowControlBar && (
              <ControlBar
                controls={controlsProp ?? defaultControlBarControls}
                showQuestionsButton={canShowQuestionsPanel}
                isQuestionsOpen={isQuestionsPanelOpen}
                onToggleQuestions={handleToggleQuestionsPanel}
              />
            )}

            {showAgentOverlay && (
              <div
                className="fedor-agent-connecting-overlay"
                role="status"
                aria-live="polite"
              >
                {showInterviewStart ? (
                  <button
                    type="button"
                    className="fedor-agent-start-button"
                    onClick={handleStartInterview}
                    disabled={isInterviewStarting}
                  >
                    Начать собеседование
                  </button>
                ) : (
                  <div className="fedor-agent-connecting-panel">
                    <span className="fedor-agent-connecting-spinner" aria-hidden="true" />
                    <span>Фёдор подключается, подождите...</span>
                  </div>
                )}
              </div>
            )}

          </div>

          <Chat />
          <ParticipantList style={participantListStyles} />
          {canShowQuestionsPanel && (
            <TextAgentQuestionsPanel
              isOpen={isQuestionsPanelOpen}
              onClose={() => setIsQuestionsPanelOpen(false)}
            />
          )}

          {path?.includes('/guest/') && <ToggleAudio />}

          <PictureInPicture />
        </LayoutContextProvider>
      )}
    </div>
  )
})
